import {
  Alert,
  Card,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import React, { useMemo } from 'react'
import {
  CensusFieldCatalogItem,
  CompanyIgnoreRuleVersion,
} from '../../api/company-ignore-api-client'
import styles from './CompanyIgnore.module.scss'
import { formatDate } from './formatDate'

interface VersionHistoryProps {
  versions: CompanyIgnoreRuleVersion[]
  fields: CensusFieldCatalogItem[]
  loading: boolean
  error?: Error | null
  asCard?: boolean
}

export default function VersionHistory({
  versions,
  fields,
  loading,
  error,
  asCard = true,
}: VersionHistoryProps) {
  const fieldLabelByKey = useMemo(() => {
    return new Map(fields.map((field) => [field.field, field.field]))
  }, [fields])

  const dataSource = useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        render: (version: number) => (
          <Typography.Text code>v{version}</Typography.Text>
        ),
      },
      {
        title: 'Ignored Fields',
        dataIndex: 'ignoredFields',
        key: 'ignoredFields',
        render: (ignoredFields: string[]) =>
          ignoredFields?.length ? (
            <div className={styles.fieldTags}>
              {ignoredFields.map((fieldKey) => (
                <Tag key={fieldKey}>
                  {fieldLabelByKey.get(fieldKey) ?? fieldKey}
                </Tag>
              ))}
            </div>
          ) : (
            '—'
          ),
      },
      {
        title: 'Channels',
        dataIndex: 'channels',
        key: 'channels',
        render: (channels: string[]) =>
          channels?.length ? (
            <div className={styles.fieldTags}>
              {channels.map((channel) => (
                <Tag key={channel} color="blue">
                  {channel}
                </Tag>
              ))}
            </div>
          ) : (
            '—'
          ),
      },
      {
        title: 'Activated At',
        dataIndex: 'activatedAt',
        key: 'activatedAt',
        render: formatDate,
      },
      {
        title: 'Deactivated At',
        dataIndex: 'deactivatedAt',
        key: 'deactivatedAt',
        render: formatDate,
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Change Reason',
        dataIndex: 'changeReason',
        key: 'changeReason',
        render: (value: string | null) => value || '—',
      },
    ],
    [fieldLabelByKey]
  )

  const content = (
    <>
      <div className={styles.panelHeader}>
        <div>
          <h3 className={styles.panelTitle}>Version History</h3>
          <p className={styles.panelSubtitle}>
            Past published ignore changes configurations.
          </p>
        </div>
      </div>
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load version history"
          description={error.message}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Table
          dataSource={dataSource}
          columns={columns as any}
          rowKey="version"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No ignore change rule versions yet' }}
        />
      )}
    </>
  )

  return asCard ? <Card>{content}</Card> : content
}
